import { create } from 'zustand';
import { cjmsApi, CJMSClient, CJMSAnalytics, CommunicationEntry } from '../api/cjmsApi';

interface CommCacheEntry {
    history: CommunicationEntry[];
    loadedAt: number;
}

interface CJMSState {
    clients: CJMSClient[];
    selectedClient: CJMSClient | null;
    analytics: CJMSAnalytics | null;
    isLoading: boolean;
    error: string | null;
    /** Per-client communication cache to avoid duplicate fetches across components */
    communicationCache: Record<string, CommCacheEntry>;

    actions: {
        loadData: () => Promise<void>;
        selectClient: (client: CJMSClient | null) => void;
        updateStage: (email: string, nextStage: string) => Promise<void>;
        refreshAnalytics: () => Promise<void>;
        // Patch the selected client's last-communication fields from the
        // Communications tab so the metric card and Intelligence Hub stay in sync.
        updateClientCommunication: (email: string, fields: {
            lastCommunication?: string;
            lastCommunicationContent?: string;
            lastCommunicationChannel?: string;
        }) => void;
        /** Load or retrieve from cache a client's full communication history */
        loadCommunicationHistory: (email: string, phone?: string, customerId?: string) => Promise<CommunicationEntry[]>;
        /** Invalidate the cache entry for a specific client */
        invalidateCommCache: (email: string) => void;
    };
}

const COMM_CACHE_TTL_MS = 60_000; // 1 minute cache lifetime

export const useCjmsStore = create<CJMSState>((set, get) => ({
    clients: [],
    selectedClient: null,
    analytics: null,
    isLoading: false,
    error: null,
    communicationCache: {},

    actions: {
        loadData: async () => {
            set({ isLoading: true, error: null });
            try {
                const clients = await cjmsApi.fetchClients();
                const analytics = await cjmsApi.fetchAnalytics();
                set({ clients, analytics, isLoading: false });
            } catch (err: any) {
                set({ error: err.message, isLoading: false });
            }
        },

        selectClient: (client) => set({ selectedClient: client }),

        updateStage: async (clientId, nextStage) => {
            set({ isLoading: true });
            try {
                const result = await cjmsApi.updateClientStage(clientId, nextStage);
                if (result.success) {
                    await get().actions.loadData();
                } else {
                    set({ error: result.error || 'Failed to update stage' });
                }
            } catch (err: any) {
                set({ error: err.message });
            } finally {
                set({ isLoading: false });
            }
        },

        refreshAnalytics: async () => {
            set({ isLoading: true });
            try {
                const analytics = await cjmsApi.fetchAnalytics();
                set({ analytics, isLoading: false });
            } catch (err: any) {
                set({ error: err.message, isLoading: false });
            }
        },

        updateClientCommunication: (email, fields) => {
            set(state => {
                const patch = (c: CJMSClient) => c.email === email ? { ...c, ...fields } : c;
                return {
                    clients: state.clients.map(patch),
                    selectedClient: state.selectedClient && state.selectedClient.email === email
                        ? { ...state.selectedClient, ...fields }
                        : state.selectedClient,
                };
            });
        },

        loadCommunicationHistory: async (email, phone?, customerId?) => {
            const state = get();
            const cached = state.communicationCache[email];
            // Return cached data if still fresh
            if (cached && Date.now() - cached.loadedAt < COMM_CACHE_TTL_MS) {
                return cached.history;
            }

            const history = await cjmsApi.fetchCommunicationHistory(email, phone, customerId);
            set(state => ({
                communicationCache: {
                    ...state.communicationCache,
                    [email]: { history, loadedAt: Date.now() },
                },
            }));
            return history;
        },

        invalidateCommCache: (email) => {
            set(state => {
                const next = { ...state.communicationCache };
                delete next[email];
                return { communicationCache: next };
            });
        },
    }
}));
